<template>
  <b-card>
    <div class="mb-2">
      <div class="d-flex align-items-center mb-2">
        <span class="mr-2 font-weight-bold">Axis Mode:</span>
        <b-form-radio-group
          v-model="localAxisMode"
          :options="axisModeOptions"
          button-variant="outline-primary"
          size="sm"
          buttons
          @input="updateSetting('axisMode', $event)"
        />
        <template v-if="localAxisMode === 'categories'">
          <span class="ml-3 mr-2 font-weight-bold">X-Axis:</span>
          <b-form-select
            v-model="localCategory1"
            :options="categoryOptionsX"
            size="sm"
            style="width: auto;"
            @input="updateSetting('category1', $event)"
          />
          <span class="ml-3 mr-2 font-weight-bold">Y-Axis:</span>
          <b-form-select
            v-model="localCategory2"
            :options="categoryOptionsY"
            size="sm"
            style="width: auto;"
            @input="updateSetting('category2', $event)"
          />
        </template>
      </div>
      <div class="d-flex align-items-center">
        <span class="mr-2 font-weight-bold">Grouping:</span>
        <b-form-select
          v-model="localGroupingMode"
          :options="groupingModeOptions"
          size="sm"
          style="width: auto;"
          @input="updateSetting('groupingMode', $event)"
        />
      </div>
    </div>
    <div v-if="loading">
      <loading-container />
    </div>
    <div v-else-if="!scatterContent || !scatterContent.SERIESDATA || scatterContent.SERIESDATA.length === 0">
      <no-data-container message="No scatter data available for the current selection" />
    </div>
    <app-echart-scatter
      v-else-if="isChartReady && scatterChartData"
      ref="chart"
      :key="chartKey"
      :option-data="scatterChartData"
      style="width: 100%; height: 500px;"
    />
  </b-card>
</template>

<script>
import { BCard, BFormRadioGroup, BFormSelect } from 'bootstrap-vue'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import NoDataContainer from '@/views/components/NoDataContainer.vue'
import AppEchartScatter from '@core/components/charts/echart/AppEchartScatter.vue'
import useAppConfig from '@core/app-config/useAppConfig'

export default {
  components: {
    BCard,
    BFormRadioGroup,
    BFormSelect,
    LoadingContainer,
    NoDataContainer,
    AppEchartScatter,
  },
  props: {
    scatterContent: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    scatterSettings: {
      type: Object,
      required: true,
    },
    axisModeOptions: {
      type: Array,
      required: true,
    },
    groupingModeOptions: {
      type: Array,
      required: true,
    },
    categoryOptions: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      scatterChartData: null,
      chartKey: 0,
      isChartReady: false,
      localAxisMode: this.scatterSettings?.axisMode || 'riskmap-score',
      localGroupingMode: this.scatterSettings?.groupingMode || 'Product Type',
      localCategory1: this.scatterSettings?.category1 || 'capitalscore',
      localCategory2: this.scatterSettings?.category2 || 'aimscore',
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
    categoryOptionsFormatted() {
      return this.categoryOptions.map(opt => {
        const displayName = opt.showname || opt.name
        // Remove [properties] or [performance] suffix
        const cleanedName = displayName.replace(/\s*\[(properties|performance) \]\s*$/i, '')
        return {
          text: cleanedName,
          value: opt.category,
        }
      })
    },
    categoryOptionsX() {
      return this.categoryOptionsFormatted.map(opt => ({
        ...opt,
        disabled: opt.value === this.localCategory2,
      }))
    },
    categoryOptionsY() {
      return this.categoryOptionsFormatted.map(opt => ({
        ...opt,
        disabled: opt.value === this.localCategory1,
      }))
    },
  },
  watch: {
    'scatterSettings.axisMode': {
      handler(newVal) {
        this.localAxisMode = newVal
      },
      immediate: true,
    },
    'scatterSettings.groupingMode': {
      handler(newVal) {
        this.localGroupingMode = newVal
      },
      immediate: true,
    },
    'scatterSettings.category1': {
      handler(newVal) {
        this.localCategory1 = newVal
      },
      immediate: true,
    },
    'scatterSettings.category2': {
      handler(newVal) {
        this.localCategory2 = newVal
      },
      immediate: true,
    },
    scatterContent: {
      handler(data) {
        if (data && data.SERIESDATA) {
          this.processScatterData(data)
        }
      },
      immediate: true,
      deep: true,
    },
    skin() {
      // Re-process data when theme changes
      if (this.scatterContent && this.scatterContent.SERIESDATA) {
        this.processScatterData(this.scatterContent)
      }
    },
  },
  mounted() {
    window.addEventListener('resize', this.resizeChart)
  },
  activated() {
    setTimeout(() => {
      this.resizeChart()
      this.attachClickHandler()
    }, 100)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    processScatterData(data) {
      if (!data || !data.SERIESDATA) {
        return
      }

      this.isChartReady = false

      // Distinct color palette for all groupings
      const distinctColors = [
        '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe', '#008080', '#e6beff', '#9a6324', '#fffac8', '#800000', '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080', '#ffffff', '#000000',
      ]

      // Cache for assigned colors
      const colorCache = {}
      let colorIndex = 0

      // Helper function to get color for a series name
      const getColorForSeries = seriesName => {
        // Extract base name from series name (e.g., "Growth Autocall (15)" -> "Growth Autocall")
        const baseName = seriesName.replace(/\s*\(\d+\)$/, '')

        // Assign colors from palette
        if (!colorCache[baseName]) {
          colorCache[baseName] = distinctColors[colorIndex % distinctColors.length]
          colorIndex += 1
        }
        return colorCache[baseName]
      }

      // Check if we're in categories mode
      const isCategoriesMode = data.SHOWCATEGORIES && (data.SHOWCATEGORIES[0] || data.SHOWCATEGORIES[1])
      const xIsCategorical = data.SHOWCATEGORIES && data.SHOWCATEGORIES[0]
      const yIsCategorical = data.SHOWCATEGORIES && data.SHOWCATEGORIES[1]

      // Track placed points to avoid overlap
      const placedPoints = []
      const minDistance = 0.08 // Minimum distance between points

      // Helper function to check if a point is too close to existing points
      const isTooClose = (x, y, placed) => {
        for (let i = 0; i < placed.length; i += 1) {
          const point = placed[i]
          const dx = x - point[0]
          const dy = y - point[1]
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < minDistance) {
            return true
          }
        }
        return false
      }

      // function to apply jitter with collision avoidance
      const applyJitter = (baseX, baseY, globalIndex) => {
        // For categorical axes, jitter within the unit quadrant; for continuous axes, apply a small fixed jitter
        const jitterX = xIsCategorical ? 0.3 : 0
        const jitterY = yIsCategorical ? 0.3 : 0
        const categoryOffsetX = xIsCategorical ? 0.5 : 0
        const categoryOffsetY = yIsCategorical ? 0.5 : 0
        const quadrantPadding = 0.1 // Padding from quadrant edges

        const seededRandom = seed => {
          const x = Math.sin(seed) * 10017
          return x - Math.floor(x)
        }

        // clamp value within quadrant boundaries (only for categorical axes)
        const clampToQuadrantX = (value, baseValue) => {
          if (!xIsCategorical) return value
          const minBound = baseValue + quadrantPadding
          const maxBound = baseValue + 1 - quadrantPadding
          return Math.max(minBound, Math.min(maxBound, value))
        }

        const clampToQuadrantY = (value, baseValue) => {
          if (!yIsCategorical) return value
          const minBound = baseValue + quadrantPadding
          const maxBound = baseValue + 1 - quadrantPadding
          return Math.max(minBound, Math.min(maxBound, value))
        }

        // multiple attempts to find a non-overlapping position
        const maxAttempts = 50
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          const seed = globalIndex * 1000 + attempt
          const angle = seededRandom(seed * 13.29898 + baseX * 78.233 + baseY * 43.758) * Math.PI * 2
          const radiusBase = seededRandom(seed * 43.758 + baseX * 12.9898 + baseY * 78.233)
          const radius = Math.sqrt(radiusBase) * 1.1

          const offsetX = Math.cos(angle) * radius * jitterX
          const offsetY = Math.sin(angle) * radius * jitterY

          let finalX = (baseX + categoryOffsetX) + offsetX
          let finalY = (baseY + categoryOffsetY) + offsetY

          // Clamp to quadrant boundaries only for categorical axes
          finalX = clampToQuadrantX(finalX, baseX)
          finalY = clampToQuadrantY(finalY, baseY)

          // Check if this position is clear
          if (!isTooClose(finalX, finalY, placedPoints)) {
            placedPoints.push([finalX, finalY])
            return [finalX, finalY]
          }
        }

        // fallback: use spiral pattern if no clear spot found
        const spiralAngle = globalIndex * 2.4
        const spiralRadius = xIsCategorical || yIsCategorical ? Math.sqrt(globalIndex * 0.1) * 0.3 : 0
        let finalX = (baseX + categoryOffsetX) + Math.cos(spiralAngle) * spiralRadius * (xIsCategorical ? 1 : 0)
        let finalY = (baseY + categoryOffsetY) + Math.sin(spiralAngle) * spiralRadius * (yIsCategorical ? 1 : 0)

        // Clamp to quadrant boundaries only for categorical axes
        finalX = clampToQuadrantX(finalX, baseX)
        finalY = clampToQuadrantY(finalY, baseY)

        placedPoints.push([finalX, finalY])
        return [finalX, finalY]
      }

      // Global counter for unique jitter across all series
      let globalPointIndex = 0

      // Process series data
      const series = data.SERIESDATA.map(seriesItem => {
        let processedData

        if (isCategoriesMode) {
          // In categories mode, apply jitter to points in quadrants
          // Use global counter to ensure unique jitter across all series
          processedData = seriesItem.data.map(point => {
            const jitteredPoint = applyJitter(point.x, point.y, globalPointIndex)
            globalPointIndex += 1
            return jitteredPoint
          })
        } else {
          // Normal mode - use actual x,y values
          processedData = seriesItem.data.map(point => [point.x, point.y])
        }

        return {
          name: seriesItem.name,
          type: 'scatter',
          data: processedData,
          symbolSize: 10,
          itemStyle: {
            color: getColorForSeries(seriesItem.name),
          },
          emphasis: {
            itemStyle: {
              cursor: 'pointer',
            },
            scale: 1.3,
          },
          // Store original data for tooltip and navigation
          originalData: seriesItem.data,
        }
      })

      // Add interior grid lines only when both axes are categorical
      if (xIsCategorical && yIsCategorical) {
        const xCategories = data.SCATTERCATEGORIES[0]?.length || 0
        const yCategories = data.SCATTERCATEGORIES[1]?.length || 0

        // Add vertical grid lines (at interior integer x positions)
        for (let x = 1; x < xCategories; x += 1) {
          series.push({
            name: 'grid-line',
            type: 'line',
            data: [[x, 0], [x, yCategories]],
            lineStyle: {
              color: '#e0e0e0',
              type: 'dashed',
              width: 1,
              opacity: 0.3,
            },
            symbol: 'none',
            silent: true,
            animation: false,
            zlevel: -1,
          })
        }

        // Add horizontal grid lines (at interior integer y positions)
        for (let y = 1; y < yCategories; y += 1) {
          series.push({
            name: 'grid-line',
            type: 'line',
            data: [[0, y], [xCategories, y]],
            lineStyle: {
              color: '#e0e0e0',
              type: 'dashed',
              width: 1,
              opacity: 0.3,
            },
            symbol: 'none',
            silent: true,
            animation: false,
            zlevel: -1,
          })
        }
      }

      const self = this
      this.scatterChartData = {
        tooltip: {
          trigger: 'item',
          confine: true,
          triggerOn: 'mousemove',
          axisPointer: {
            type: 'cross',
            crossStyle: {
              color: '#999',
              type: 'dashed',
            },
            snap: true,
          },
          formatter(params) {
            // Filter out grid lines
            if (params.seriesName === 'grid-line') return ''

            if (params.componentSubType === 'scatter' && params.seriesName) {
              // Find the original data point
              const seriesData = self.scatterChartData.series.find(s => s.name === params.seriesName)
              if (seriesData && seriesData.originalData) {
                const point = seriesData.originalData[params.dataIndex]
                if (point) {
                  let tooltip = `<b>${point.reportnumber} ${point.provider}</b><br/>`
                  tooltip += `${point.reporttitle}<br/>`
                  tooltip += `${point.seriesname}<br/>`
                  // Only show riskmap and overall score in non-categories mode
                  if (!isCategoriesMode) {
                    tooltip += `Riskmap: ${point.riskmap}<br/>`
                    tooltip += `Overall Score: ${point.overallscore}<br/>`
                  }
                  return tooltip
                }
              }
            }
            return 'No data'
          },
        },
        legend: {
          show: true,
          left: 'center',
          top: 0,
          selector: false,
          data: series.filter(s => s.name !== 'grid-line').map(s => s.name),
        },
        grid: {
          left: 80,
          right: 30,
          bottom: 60,
          top: 70,
          containLabel: false,
        },
        xAxis: {
          type: 'value',
          name: data.XAXISTITLE || 'Riskmap',
          nameLocation: 'middle',
          nameGap: 35,
          min: xIsCategorical ? 0 : undefined,
          max: xIsCategorical && data.SCATTERCATEGORIES[0] ? data.SCATTERCATEGORIES[0].length : undefined,
          interval: xIsCategorical ? 0.5 : undefined,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
            formatter: xIsCategorical ? value => {
              // Only show labels at half-integer positions (quadrant centers)
              if (value % 1 === 0) return ''
              const index = Math.floor(value)
              return data.SCATTERCATEGORIES[0][index] || ''
            } : undefined,
          },
          axisTick: {
            show: !xIsCategorical,
          },
          axisLine: {
            show: true,
          },
          splitLine: {
            show: !xIsCategorical,
            alignWithLabel: false,
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
          scale: !xIsCategorical,
          axisPointer: {
            show: true,
            snap: true,
            label: {
              show: !xIsCategorical,
            },
          },
        },
        yAxis: {
          type: 'value',
          name: data.YAXISTITLE || 'Overall Score',
          nameLocation: 'middle',
          nameGap: 50,
          min: yIsCategorical ? 0 : undefined,
          max: yIsCategorical && data.SCATTERCATEGORIES[1] ? data.SCATTERCATEGORIES[1].length : undefined,
          interval: yIsCategorical ? 0.5 : undefined,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
            formatter: yIsCategorical ? value => {
              // Only show labels at half-integer positions (quadrant centers)
              if (value % 1 === 0) return ''
              const index = Math.floor(value)
              return data.SCATTERCATEGORIES[1][index] || ''
            } : undefined,
          },
          axisTick: {
            show: !yIsCategorical,
          },
          axisLine: {
            show: true,
          },
          splitLine: {
            show: !yIsCategorical,
            alignWithLabel: false,
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
          scale: !yIsCategorical,
          axisPointer: {
            show: true,
            snap: true,
            label: {
              show: !yIsCategorical,
            },
          },
        },
        series,
      }

      this.chartKey += 1

      this.$nextTick(() => {
        this.isChartReady = true
        setTimeout(() => {
          this.resizeChart()
          this.attachClickHandler()
        }, 100)
      })
    },
    resizeChart() {
      if (this.$refs.chart) {
        this.$refs.chart.resize()
      }
    },
    updateSetting(key, value) {
      // Emit event to parent to update settings
      this.$emit('update-scatter-settings', { key, value })
    },
    attachClickHandler() {
      if (this.$refs.chart) {
        const chartInstance = this.$refs.chart.getChartInstance()
        if (chartInstance) {
          chartInstance.off('click')

          chartInstance.on('click', params => {
            if (params.componentSubType === 'scatter' && params.seriesName) {
              const seriesData = this.scatterChartData.series.find(s => s.name === params.seriesName)
              if (seriesData && seriesData.originalData) {
                const point = seriesData.originalData[params.dataIndex]
                if (point && point.reportnumber) {
                  this.$router.push({
                    name: 'product-report',
                    params: {
                      reportNumber: point.reportnumber,
                    },
                  })
                }
              }
            }
          })
        }
      }
    },
  },
}
</script>
